// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import {
  Box,
  Button,
  Center,
  Checkbox,
  FocusTrap,
  Group,
  Modal,
  Radio,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { Dropzone } from '@mantine/dropzone';
import { isNotEmpty, useForm } from '@mantine/form';
import type { Attachment, PractitionerQualification } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import dayjs from 'dayjs';
import { type JSX, useEffect, useState } from 'react';

import organizationsData from '../../../../data/organizations.json';
import {
  APPLICATION_ID_SYSTEM,
  CREDENTIAL_SYSTEM,
  getQualificationExtension,
  getQualificationFileAttachment,
  PROVIDER_NPI_SYSTEM,
} from '../../../utils/practitioner';
import { LICENSE_STATUS_INITIAL, LICENSE_STATUS_OPTIONS } from '../constants';

const PAYER_OPTIONS = organizationsData.entry
  .filter((e) => e.resource.type?.[0]?.coding?.[0]?.code === 'ins')
  .map((e) => ({ value: e.resource.name, label: e.resource.name }));

interface SubmitCredentialModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (qualification: PractitionerQualification) => Promise<void>;
  saving: boolean;
  initialQualification?: PractitionerQualification;
}

export function SubmitCredentialModal({
  opened,
  onClose,
  onSubmit,
  saving,
  initialQualification,
}: SubmitCredentialModalProps): JSX.Element {
  const medplum = useMedplum();
  const [file, setFile] = useState<File | null>(null);
  const [existingAttachment, setExistingAttachment] = useState<Attachment | null>(null);
  const [uploading, setUploading] = useState(false);
  const isEditing = !!initialQualification;

  const form = useForm({
    initialValues: {
      payor: '',
      applicationId: '',
      providerType: '',
      providerNpi: '',
      groupId: '',
      planId: '',
      effectiveDate: null as Date | null,
      expirationDate: null as Date | null,
      status: LICENSE_STATUS_INITIAL,
      hasDocumentation: 'no' as 'yes' | 'no',
      attestation: false,
    },
    validate: {
      payor: isNotEmpty('Payor is required'),
      applicationId: isNotEmpty('Application ID is required'),
      providerType: isNotEmpty('Provider type is required'),
      providerNpi: isNotEmpty('Provider NPI is required'),
      groupId: isNotEmpty('Group ID is required'),
      planId: isNotEmpty('Plan ID is required'),
      effectiveDate: isNotEmpty('Effective date is required'),
      expirationDate: (v, values) => {
        if (!v) {
          return 'Expiration date is required';
        }
        if (values.effectiveDate && v <= values.effectiveDate) {
          return 'Expiration date must be after effective date';
        }
        return null;
      },
      attestation: isNotEmpty('You must attest to the accuracy of this information'),
    },
  });

  useEffect(() => {
    if (!opened) {
      return;
    }
    if (initialQualification) {
      const attachment = getQualificationFileAttachment(initialQualification) ?? null;
      setExistingAttachment(attachment);
      form.setValues({
        payor: initialQualification.issuer?.display ?? '',
        planId: initialQualification.identifier?.[0]?.value ?? '',
        providerNpi: initialQualification.identifier?.find((id) => id.system === PROVIDER_NPI_SYSTEM)?.value ?? '',
        applicationId: initialQualification.identifier?.find((id) => id.system === APPLICATION_ID_SYSTEM)?.value ?? '',
        groupId: getQualificationExtension(initialQualification, 'groupId'),
        providerType: getQualificationExtension(initialQualification, 'providerType'),
        effectiveDate: initialQualification.period?.start
          ? new Date(`${initialQualification.period.start}T00:00:00`)
          : null,
        expirationDate: initialQualification.period?.end
          ? new Date(`${initialQualification.period.end}T00:00:00`)
          : null,
        status: initialQualification.extension?.find((e) => e.url === 'status')?.valueString ?? LICENSE_STATUS_INITIAL,
        hasDocumentation: attachment ? 'yes' : 'no',
        attestation: false,
      });
    } else {
      form.reset();
      setFile(null);
      setExistingAttachment(null);
    }
  }, [opened, initialQualification, form.setValues, form.reset]);

  const handleClose = (): void => {
    form.reset();
    setFile(null);
    setExistingAttachment(null);
    onClose();
  };

  const handleSubmit = form.onSubmit(async (values) => {
    let fileAttachment: Attachment | undefined;

    if (values.hasDocumentation === 'yes' && file) {
      setUploading(true);
      try {
        const binary = await medplum.createBinary({
          data: file,
          contentType: file.type,
          filename: file.name,
        });
        fileAttachment = { url: binary.url, contentType: file.type, title: file.name };
      } finally {
        setUploading(false);
      }
    } else if (values.hasDocumentation === 'yes' && existingAttachment) {
      fileAttachment = existingAttachment;
    }

    const extensions: PractitionerQualification['extension'] = [{ url: 'status', valueString: values.status }];
    if (values.providerType) {
      extensions.push({ url: 'providerType', valueString: values.providerType });
    }
    if (values.groupId) {
      extensions.push({ url: 'groupId', valueString: values.groupId });
    }
    if (fileAttachment) {
      extensions.push({ url: 'file', valueAttachment: fileAttachment });
    }

    const identifiers: PractitionerQualification['identifier'] = [{ value: values.planId }];
    if (values.providerNpi) {
      identifiers.push({ system: PROVIDER_NPI_SYSTEM, value: values.providerNpi });
    }
    if (values.applicationId) {
      identifiers.push({ system: APPLICATION_ID_SYSTEM, value: values.applicationId });
    }

    const qualification: PractitionerQualification = {
      code: { coding: [{ system: CREDENTIAL_SYSTEM, code: 'payor-credential' }] },
      issuer: { display: values.payor },
      identifier: identifiers,
      period: {
        start: values.effectiveDate ? dayjs(values.effectiveDate).format('YYYY-MM-DD') : undefined,
        end: values.expirationDate ? dayjs(values.expirationDate).format('YYYY-MM-DD') : undefined,
      },
      extension: extensions,
    };

    try {
      await onSubmit(qualification);
      form.reset();
      setFile(null);
      setExistingAttachment(null);
    } catch {
      // error notification already shown by the parent handler
    }
  });

  const busy = saving || uploading;

  const labelProps = { fw: 600, fz: 16 };

  return (
    <Modal opened={opened} onClose={handleClose} withCloseButton={false} size="xl" radius="lg">
      <Stack gap={0} m="lg">
        <Center>
          <Title order={2}>{isEditing ? 'Edit Payor Credential' : 'Submit New Payor Credential'}</Title>
        </Center>
        <Center>
          <Box maw={400}>
            <Text ta="center" size="sm" mb={0} mt="sm">
              Please enter the following information to add a new credential to your Polaris Sleep provider account.
            </Text>
          </Box>
        </Center>
        <form onSubmit={handleSubmit}>
          <Box bg="gray.0" p="xl" style={{ borderRadius: 8 }}>
            <Stack gap="md">
              <FocusTrap.InitialFocus />
              <Select
                label="Payor"
                placeholder="Select a payor"
                required
                labelProps={labelProps}
                data={PAYER_OPTIONS}
                {...form.getInputProps('payor')}
              />
              <TextInput
                label="Application ID"
                placeholder="e.g. 1294910"
                required
                labelProps={labelProps}
                {...form.getInputProps('applicationId')}
              />
              <TextInput
                label="Provider Type"
                placeholder="e.g. MD"
                required
                labelProps={labelProps}
                {...form.getInputProps('providerType')}
              />
              <TextInput
                label="Provider NPI"
                placeholder="e.g. 1234567890"
                required
                labelProps={labelProps}
                {...form.getInputProps('providerNpi')}
              />
              <TextInput
                label="Group ID"
                placeholder="e.g. 12940192"
                required
                labelProps={labelProps}
                {...form.getInputProps('groupId')}
              />
              <TextInput
                label="Plan ID"
                placeholder="e.g. 1209421"
                required
                labelProps={labelProps}
                {...form.getInputProps('planId')}
              />
              <Select
                label="Status"
                data={LICENSE_STATUS_OPTIONS.filter((o) => ['Awaiting Review', 'Pending', 'Active'].includes(o.value))}
                labelProps={labelProps}
                {...form.getInputProps('status')}
              />
              <Group grow>
                <DatePickerInput
                  label="Effective Date / Credentialed Date"
                  placeholder="MM/DD/YYYY"
                  required
                  labelProps={labelProps}
                  {...form.getInputProps('effectiveDate')}
                />
                <DatePickerInput
                  label="Expiration Date"
                  placeholder="MM/DD/YYYY"
                  required
                  labelProps={labelProps}
                  {...form.getInputProps('expirationDate')}
                />
              </Group>
              <Box style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 8 }} pt="md">
                <Radio.Group
                  label="Do you have any documentation to accompany this new payor credential?"
                  required
                  labelProps={labelProps}
                  px="md"
                  {...form.getInputProps('hasDocumentation')}
                >
                  <Stack gap={4} mt={4} mb="sm">
                    <Radio value="yes" label="Yes" />
                    <Radio value="no" label="No" />
                  </Stack>
                </Radio.Group>
                {form.values.hasDocumentation === 'yes' && (
                  <Box bg="gray.0" p="md" mt="md">
                    <Text fw={600} fz={16} mb={8}>
                      Please upload below
                    </Text>
                    <Dropzone
                      onDrop={(files) => setFile(files[0] ?? null)}
                      accept={['application/pdf']}
                      maxFiles={1}
                      maxSize={10 * 1024 * 1024}
                      disabled={busy}
                      bg="white"
                      styles={{
                        root: {
                          border: '2px dashed #640040',
                          borderRadius: 8,
                        },
                      }}
                    >
                      <Stack align="center" gap={4} style={{ pointerEvents: 'none' }}>
                        <Text fw={600} fz={14} c="#640040">
                          {file
                            ? file.name
                            : (existingAttachment?.title ?? existingAttachment?.url?.split('/').pop() ?? 'Upload File')}
                        </Text>
                        <Text fz={12} c="dimmed">
                          Accepted File Formats: PDF
                        </Text>
                      </Stack>
                    </Dropzone>
                  </Box>
                )}
              </Box>
              <Box>
                <Text fw={600} fz={16} mb={8}>
                  Attestation of Accuracy <span style={{ color: 'var(--mantine-color-error)' }}>*</span>
                </Text>
                <Checkbox
                  label="By checking this box, I confirm that the information I have entered is accurate to the extent of my knowledge and acknowledge that misrepresenting my licensure may result in criminal charges."
                  {...form.getInputProps('attestation', { type: 'checkbox' })}
                />
              </Box>
            </Stack>
          </Box>
          <Group justify="center" mt="xl" gap="sm">
            <Button variant="outline" color="polaris" h={50} fz={18} fw={600} onClick={handleClose} disabled={busy}>
              Back
            </Button>
            <Button type="submit" color="polaris" h={50} fz={18} fw={600} loading={busy}>
              {isEditing ? 'Save Changes' : 'Submit'}
            </Button>
          </Group>
        </form>
      </Stack>
    </Modal>
  );
}
